import { useMemo } from "react";
import { useLoginPageSetup } from "./hooks/useLoginPageSetup";
import { useLoginViewState } from "./hooks/useLoginViewState";
import { ErrorAlert } from "./components/ErrorAlert";
import { DotGrid } from "./components/DotGrid";
import { FormCard } from "./components/FormCard";
import { LeftPanel } from "./components/LeftPanel";
import { TrustBar } from "./components/TrustBar";
import { WaveCanvas } from "./components/WaveCanvas";
import { AccountSelectionView } from "./views/AccountSelectionView";
import { ConfirmEmailView } from "./views/ConfirmEmailView";
import { ForgotPasswordSentView } from "./views/ForgotPasswordSentView";
import { ForgotPasswordView } from "./views/ForgotPasswordView";
import { LoginView } from "./views/LoginView";
import { ResetPasswordView } from "./views/ResetPasswordView";
import { UpdatePasswordView } from "./views/UpdatePasswordView";
import { VerifyCodeView } from "./views/VerifyCodeView";
import { safeDecodeURIComponent } from "./utils/validators";
import { KlipboardCrmInline } from "@/components/logos/KlipboardCrmInline";

export const LoginPage = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const { view, viewExtra, handleViewChange, ssoError } = useLoginViewState();

  useLoginPageSetup();

  const viewRenderers = {
    login:              () => <LoginView onViewChange={handleViewChange} />,
    forgotPassword:     () => <ForgotPasswordView onViewChange={handleViewChange} />,
    forgotPasswordSent: () => <ForgotPasswordSentView onViewChange={handleViewChange} />,
    verifyCode:         () => <VerifyCodeView onViewChange={handleViewChange} />,
    updatePassword:     () => (
      <UpdatePasswordView
        passwordDays={viewExtra.passwordDays}
        passwordStatus={viewExtra.passwordStatus}
      />
    ),
    resetPassword: () => (
      <ResetPasswordView
        account={params.get("account") ?? ""}
        email={params.get("email") ?? ""}
        token={params.get("token") ?? ""}
        onViewChange={handleViewChange}
      />
    ),
    confirmEmail: () => (
      <ConfirmEmailView
        confirmation={params.get("confirmation") ?? ""}
        onViewChange={handleViewChange}
      />
    ),
    accountSelection: () => (
      <AccountSelectionView
        customerMode={viewExtra.customerMode === "true"}
        onViewChange={handleViewChange}
      />
    ),
  };

  return (
    <>
      <div className="relative w-screen bg-[#1E2235] min-h-screen lg:h-screen lg:overflow-hidden">
        {/* Full-page background effects */}
        <WaveCanvas />
        <DotGrid />

        {/* ── Desktop ── */}
        <div className="hidden lg:flex absolute inset-0 items-center justify-center">
          <div
            className="grid w-[min(1280px,calc(100vw-6rem))] h-[min(820px,calc(100vh-3rem))] items-start"
            style={{
              gridTemplateColumns: "58% 42%",
              gap: "clamp(1rem, 2vw, 2.5rem)",
            }}
          >
            {/* Left — features content, overlaid on dark background */}
            <LeftPanel />

            {/* Right — form card, floated right within its column */}
            <div className="flex flex-col items-end">
              <div className="w-full max-w-lg">
                {ssoError && view === "login" && (
                  <ErrorAlert message={safeDecodeURIComponent(ssoError)} />
                )}
                <FormCard className="p-[clamp(1.75rem,2.5vw,3rem)] mb-[clamp(0.75rem,1.2vw,1.5rem)]">
                  {viewRenderers[view]()}
                </FormCard>
                <TrustBar />
              </div>
            </div>
          </div>
        </div>

        {/* ── Mobile / portrait ── */}
        <div className="lg:hidden relative z-10 flex flex-col items-center w-full max-w-lg mx-auto px-5 py-4">
          {ssoError && view === "login" && (
            <ErrorAlert message={safeDecodeURIComponent(ssoError)} />
          )}
          <FormCard className="p-5 lg:p-[clamp(1.75rem,2.5vw,3rem)] mb-3">
            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#F3F4F6]">
              <KlipboardCrmInline iconSize={26} textColor="#333333" />
            </div>
            {viewRenderers[view]()}
          </FormCard>
          <TrustBar />
        </div>
      </div>
    </>
  );
};
